import { ChatPageContextData, ChatDetailsResponse } from '@choco-admin/types';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
} from 'react';

interface ExtendedChatPageContextData extends ChatPageContextData {
  chatData: ChatDetailsResponse | null;
  setChatData: (data: ChatDetailsResponse | null) => void;
  updateChatData: (
    updater: (prev: ChatDetailsResponse | null) => ChatDetailsResponse | null
  ) => void;
}

const ChatPageContext = createContext<ExtendedChatPageContextData | undefined>(
  undefined
);

export const useChatPageContext = (): ExtendedChatPageContextData => {
  const context = useContext(ChatPageContext);
  if (!context) {
    throw new Error(
      'useChatPageContext must be used within a ChatPageContextProvider'
    );
  }
  return context;
};

interface ChatPageContextProviderProps {
  children: ReactNode;
  initialChatData?: ChatDetailsResponse | null;
}

export const ChatPageContextProvider: React.FC<
  ChatPageContextProviderProps
> = ({ children, initialChatData = null }) => {
  const [pageErrorFields, setPageErrorFields] = useState<string[]>([]);
  const [chatData, setChatData] = useState<ChatDetailsResponse | null>(
    initialChatData
  );

  // Reconciliation model — context holds two kinds of truth that must stay
  // consistent:
  //
  //   1. Immediate mutation truth (the #102 pattern). Add/remove-user,
  //      buyer-switch and distributor-notification flows write server-true data
  //      straight into `chatData` via `updateChatData` so the UI reflects the
  //      change without a round-trip. Those mutations run under the app-wide
  //      Apollo `no-cache` default (see graphql-client `defaultOptions.mutate`),
  //      so they never re-emit `GetChatData`. `initialChatData` therefore keeps
  //      its reference across them and the effect below stays dormant — the
  //      optimistic write is preserved.
  //
  //   2. Server reconciliation via prop. Only a genuine `GetChatData` refetch
  //      produces newer server truth — e.g. #123's assign-distributor flow uses
  //      `refetchQueries: ['GetChatData'], awaitRefetchQueries: true`, which
  //      updates the PROP chain, not context. `mapToChatDetailsResponse` returns
  //      a NEW object per response, so a completed refetch is observable here as
  //      a new `initialChatData` reference. We must adopt it, otherwise the
  //      seeded (stale) context permanently masks the fresh data — the bug where
  //      an assigned distributor (and its bot user) never appear until a reload.
  //
  // Keying on reference identity is provably correct: the only writer of a new
  // `initialChatData` reference is a fresh mapped refetch response; local
  // `updateChatData` writes never change the prop, so they are never clobbered.
  const syncedInitialRef = useRef<ChatDetailsResponse | null>(initialChatData);
  useEffect(() => {
    if (initialChatData !== syncedInitialRef.current) {
      syncedInitialRef.current = initialChatData;
      setChatData(initialChatData);
    }
  }, [initialChatData]);

  const updateChatData = (
    updater: (prev: ChatDetailsResponse | null) => ChatDetailsResponse | null
  ) => {
    setChatData(updater);
  };

  const contextValue: ExtendedChatPageContextData = {
    pageErrorFields,
    setPageErrorFields,
    chatData,
    setChatData,
    updateChatData,
  };

  return (
    <ChatPageContext.Provider value={contextValue}>
      {children}
    </ChatPageContext.Provider>
  );
};
